import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchUserOrders } from '../../redux/actions/authActions';
import styles from '../../sass/orders.module.scss';

const Orders = () => {
  const dispatch = useDispatch();
  const { orders, loader, errors, token } = useSelector((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const initialRenderRef = useRef(true);

  useEffect(() => {
    if (token) {
      dispatch(fetchUserOrders(currentPage));
    }
  }, [dispatch, token, currentPage]);

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const formatDate = (dateString) => {
    if (!dateString) {
      return '--';
    }

    const parsedDate = new Date(dateString);

    if (Number.isNaN(parsedDate.getTime())) {
      return '--';
    }

    return parsedDate.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount)) {
      return '--';
    }

    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(numericAmount);
  };

  const toNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const calculateOrderAmount = (order) => {
    const primaryAmount = [
      order?.payable_amount,
      order?.final_amount,
      order?.final_total,
      order?.amount,
      order?.grand_total,
    ]
      .map(toNumber)
      .find((val) => val > 0);

    if (primaryAmount) {
      return primaryAmount;
    }

    const mrpTotal = toNumber(order?.total);
    const discountComponent = toNumber(
      order?.discounted_total ??
      order?.discountedTotal ??
      order?.discount_total ??
      order?.discount
    );
    const deliveryCharges = toNumber(
      order?.delivery_charges ??
      order?.delivery_charge ??
      order?.deliveryCharges ??
      order?.shipping_charges ??
      order?.shipping
    );
    const codCharges = toNumber(
      order?.cod_amt ??
      order?.cod_amount ??
      order?.codCharges ??
      order?.cod_charge ??
      order?.cod
    );
    const taxAmount = toNumber(
      order?.tax_amount ??
      order?.tax ??
      order?.gst_amount ??
      order?.gst
    );

    const discountedSubtotal = Math.max(mrpTotal - discountComponent, 0);
    const recomputedTotal = discountedSubtotal + deliveryCharges + codCharges + taxAmount;

    if (recomputedTotal > 0) {
      return recomputedTotal;
    }

    return mrpTotal;
  };

  const formatStatusLabel = (value) => {
    if (typeof value !== 'string' || !value.trim()) {
      return '--';
    }

    return value
      .trim()
      .split(/[\s_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusClassName = (statusValue) => {
    if (typeof statusValue !== 'string' || !statusValue.trim()) {
      return `${styles.statusBadge} ${styles['status-default']}`;
    }

    const normalized = statusValue
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-');

    const variantClass = styles[`status-${normalized}`] || styles['status-default'];

    return `${styles.statusBadge} ${variantClass}`;
  };

  if (loader) {
    return (
      <div className={styles.ordersPage}>
        <div className={styles.pageContainer}>
          <div className={styles.loaderContainer}>
            <span className={styles.loaderSpinner} aria-hidden="true" />
            <span className={styles.loaderText}>Loading orders...</span>
          </div>
        </div>
      </div>
    );
  }

  if (errors) {
    return (
      <div className={styles.ordersPage}>
        <div className={styles.pageContainer}>
          <div className={styles.errorState}>
            <h2 className={styles.errorTitle}>Error loading orders</h2>
            <p className={styles.errorMessage}>
              {typeof errors === 'string' ? errors : JSON.stringify(errors)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const ordersData = orders?.orders?.data || [];
  const pagination = orders?.orders || {};

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      setCurrentPage(page);
    }
  };

  return (
    <div className={styles.ordersPage}>
      <div className={styles.pageContainer}>
        <div className={styles.header}>
         
          <h1 className={styles.title}>My Orders</h1>
        </div>

        {ordersData.length === 0 ? (
          <div className={styles.emptyState}>
            <h3 className={styles.emptyTitle}>No orders found</h3>
            <p className={styles.emptyDescription}>
              You haven&apos;t placed any orders yet. Start exploring our catalogue to see your orders here.
            </p>
            <Link to="/" className={styles.primaryAction}>
              Start Shopping
            </Link>
          </div>
        ) : (
          <>
            <div className={styles.tableCard}>
              <div className={styles.tableWrapper}>
                <table className={styles.ordersTable}>
                  <thead>
                    <tr>
                      <th className={styles.headerCell}>Order</th>
                      <th className={styles.headerCell}>Items</th>
                      <th className={styles.headerCell}>Status</th>
                      <th className={`${styles.headerCell} ${styles.numericCell}`}>Amount</th>
                      <th className={styles.headerCell}>Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordersData.map((order) => {
                      const statusValue = order.status || order.payment_status || '--';
                      const products = Array.isArray(order.products) ? order.products : [];
                      const totalQuantity = products.reduce((count, product) => {
                        const qty = Number(product?.quantity);
                        return count + (Number.isFinite(qty) ? qty : 0);
                      }, 0);
                      const itemsCount = totalQuantity || products.length;
                      const payableAmount = calculateOrderAmount(order);

                      return (
                        <tr key={order.id || order.po_number} className={styles.dataRow}>
                          <td className={styles.dataCell} data-label="Order">
                            <div className={styles.cellContent}>
                              <span className={styles.orderId}>{order.po_number || order.id}</span>
                              <span className={styles.mutedText}>Placed on {formatDate(order.po_date)}</span>
                            </div>
                          </td>
                          <td className={styles.dataCell} data-label="Items">
                             <div className={`${styles.cellContent} ${styles.itemsCellContent}`}>
                               {products.length > 0 ? (
                                 <>
                                   <table className={styles.productTable}>
                                     <thead>
                                       <tr>
                                         <th className={styles.productTableHeader}>Product</th>
                                         <th className={styles.productTableHeader}>Qty</th>
                                         <th className={styles.productTableHeader}>Rate</th>
                                       </tr>
                                     </thead>
                                     <tbody>
                                       {products.map((product, index) => (
                                         <tr key={product?.id || index}>
                                           <td
                                             className={styles.productTableCell}
                                             data-label="Product"
                                             title={product?.name || 'Product'}
                                           >
                                             {product?.name || 'Product'}
                                           </td>
                                           <td
                                             className={`${styles.productTableCell} ${styles.productTableCellNumeric}`}
                                             data-label="Qty"
                                           >
                                             {product?.quantity || 1}
                                           </td>
                                           <td
                                             className={`${styles.productTableCell} ${styles.productTableCellNumeric}`}
                                             data-label="Rate"
                                           >
                                             {formatCurrency(product?.offer_price || product?.price || 0)}
                                           </td>
                                         </tr>
                                       ))}
                                     </tbody>
                                   </table>
                                   <span className={styles.productBadge}>
                                     {itemsCount} item{itemsCount === 1 ? '' : 's'} total
                                   </span>
                                 </>
                               ) : (
                                 <span className={styles.mutedText}>No items</span>
                               )}
                             </div>
                           </td>
                          <td className={styles.dataCell} data-label="Status">
                            <div className={styles.cellContent}>
                              <span className={getStatusClassName(statusValue)}>{formatStatusLabel(statusValue)}</span>
                            </div>
                          </td>
                          <td className={`${styles.dataCell} ${styles.numericCell}`} data-label="Amount">
                            <div className={styles.cellContent}>
                              <span className={styles.amount}>{formatCurrency(payableAmount)}</span>
                            </div>
                          </td>
                          <td className={styles.dataCell} data-label="Payment">
                            <div className={styles.cellContent}>
                              <span className={styles.paymentMethod}>{order.payment_type || order.payment_method || '--'}</span>
                              <span className={styles.paymentStatus}>{formatStatusLabel(order.payment_status)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            {pagination.last_page > 1 && (
              <div className={styles.pagination}>
                {pagination.links?.map((link, index) => {
                  let displayLabel = link.label;
                  let pageNumber = null;

                  if (link.label.includes('&laquo;')) {
                    displayLabel = '« Previous';
                    pageNumber = currentPage - 1;
                  } else if (link.label.includes('&raquo;')) {
                    displayLabel = 'Next »';
                    pageNumber = currentPage + 1;
                  } else {
                    const num = parseInt(link.label);
                    if (!isNaN(num)) {
                      pageNumber = num;
                      displayLabel = link.label;
                    }
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => pageNumber && handlePageChange(pageNumber)}
                      disabled={!link.url || link.active}
                      className={`${styles.paginationButton} ${link.active ? styles.active : ''}`}
                    >
                      {displayLabel}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Orders;
